import { useEffect, useState } from 'react';
import type { GameController } from '../../core/GameController';
import type { GameSettings, GameSnapshot, InputAction, ResolutionId } from '../../core/types';
import { formatInputCode as prettyKey } from '../../input/InputManager';
import { BackButton, MenuFrame } from '../components/MenuFrame';

type SettingsTab = 'video' | 'audio' | 'gameplay' | 'controls';

const RESOLUTIONS: ResolutionId[] = ['1280x720', '1600x900', '1920x1080', '2560x1440'];
const ACTION_LABELS: Record<InputAction, string> = {
  accelerate: 'Accelerate',
  brake: 'Brake / Reverse',
  left: 'Steer Left',
  right: 'Steer Right',
  handbrake: 'Handbrake',
  restart: 'Instant Restart',
  reset: 'Checkpoint Reset',
  pause: 'Pause',
};

function SettingRow({ label, description, children }: { label: string; description: string; children: React.ReactNode }) {
  return (
    <div className="setting-row">
      <div><b>{label}</b><span>{description}</span></div>
      <div className="setting-row__control">{children}</div>
    </div>
  );
}

function Segmented<T extends string>({ value, options, onChange }: { value: T; options: Array<[T, string]>; onChange(value: T): void }) {
  return (
    <div className="segmented-control">
      {options.map(([id, label]) => (
        <button key={id} className={value === id ? 'is-active' : ''} onClick={() => onChange(id)} aria-pressed={value === id}>{label}</button>
      ))}
    </div>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange(value: boolean): void; label: string }) {
  return (
    <button className={`toggle ${checked ? 'is-on' : ''}`} onClick={() => onChange(!checked)} role="switch" aria-checked={checked} aria-label={label}>
      <i /> <span>{checked ? 'On' : 'Off'}</span>
    </button>
  );
}

function VolumeSlider({ value, onChange, label }: { value: number; onChange(value: number): void; label: string }) {
  return (
    <div className="volume-control">
      <input aria-label={label} type="range" min="0" max="1" step="0.01" value={value} onChange={(event) => onChange(Number(event.target.value))} />
      <b>{Math.round(value * 100)}</b>
    </div>
  );
}

export function SettingsScreen({ controller, snapshot }: { controller: GameController; snapshot: GameSnapshot }) {
  const [tab, setTab] = useState<SettingsTab>('video');
  const [rebinding, setRebinding] = useState<InputAction | null>(null);
  const settings = snapshot.settings;
  const patch = (update: Partial<GameSettings>) => controller.updateSettings(update);

  useEffect(() => {
    if (!rebinding) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      controller.rebindAction(rebinding, event.code);
      setRebinding(null);
    };
    window.addEventListener('keydown', onKeyDown, { capture: true, once: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [controller, rebinding]);

  return (
    <div className={`screen-layer settings-screen ${snapshot.settingsFromPause ? 'settings-screen--paused' : ''}`}>
      <MenuFrame
        wide
        eyebrow={snapshot.settingsFromPause ? 'Race paused' : 'System configuration'}
        title="Settings"
        subtitle="Shape the presentation without changing the competition."
        footer={<BackButton onClick={() => controller.closeSettings()} label={snapshot.settingsFromPause ? 'Back to Pause' : 'Back'} />}
      >
        <div className="settings-layout">
          <nav className="settings-tabs" aria-label="Settings categories">
            {([
              ['video', '▰', 'Video'],
              ['audio', '♪', 'Audio'],
              ['gameplay', '◫', 'Gameplay'],
              ['controls', '⌨', 'Controls'],
            ] as Array<[SettingsTab, string, string]>).map(([id, icon, label]) => (
              <button key={id} className={tab === id ? 'is-active' : ''} onClick={() => setTab(id)}>
                <span aria-hidden="true">{icon}</span>{label}
              </button>
            ))}
          </nav>

          <div className="settings-panel">
            {tab === 'video' && (
              <>
                <h2>Video</h2>
                <p>Desktop display and procedural scenery options.</p>
                <SettingRow label="Resolution" description="Changes the game window size in the Windows build.">
                  <select value={settings.resolution} onChange={(event) => patch({ resolution: event.target.value as ResolutionId })} aria-label="Resolution">
                    {RESOLUTIONS.map((resolution) => <option key={resolution}>{resolution}</option>)}
                  </select>
                </SettingRow>
                <SettingRow label="Fullscreen" description="Use the current display without a window border.">
                  <Toggle checked={settings.fullscreen} onChange={(fullscreen) => patch({ fullscreen })} label="Fullscreen" />
                </SettingRow>
                <SettingRow label="Vertical Sync" description="Synchronize presentation to the display refresh cycle.">
                  <Toggle checked={settings.vsync} onChange={(vsync) => patch({ vsync })} label="Vertical Sync" />
                </SettingRow>
                <SettingRow label="Scenery Density" description="Adjust generated environmental detail; road geometry never changes.">
                  <Segmented
                    value={settings.sceneryDensity}
                    options={[["low", 'Low'], ["medium", 'Medium'], ["high", 'High']]}
                    onChange={(sceneryDensity) => patch({ sceneryDensity })}
                  />
                </SettingRow>
              </>
            )}

            {tab === 'audio' && (
              <>
                <h2>Audio</h2>
                <p>Every sound is synthesized in real time—no external sound pack required.</p>
                <SettingRow label="Master Volume" description="Overall output level.">
                  <VolumeSlider label="Master volume" value={settings.masterVolume} onChange={(masterVolume) => patch({ masterVolume })} />
                </SettingRow>
                <SettingRow label="Effects Volume" description="Menus, countdown, checkpoints, boosters and impacts.">
                  <VolumeSlider label="Effects volume" value={settings.effectsVolume} onChange={(effectsVolume) => patch({ effectsVolume })} />
                </SettingRow>
                <SettingRow label="Engine Volume" description="Reactive engine tone and tire slide audio.">
                  <VolumeSlider label="Engine volume" value={settings.engineVolume} onChange={(engineVolume) => patch({ engineVolume })} />
                </SettingRow>
                <button className="test-audio-button" onClick={() => controller.previewAudio()}>Test generated audio</button>
              </>
            )}

            {tab === 'gameplay' && (
              <>
                <h2>Gameplay</h2>
                <p>Readout preferences only. Vehicle performance remains identical.</p>
                <SettingRow label="Speed Display" description="Choose the HUD speed unit.">
                  <Segmented value={settings.speedUnit} options={[["mph", 'MPH'], ["kmh", 'KM/H']]} onChange={(speedUnit) => patch({ speedUnit })} />
                </SettingRow>
                <div className="settings-callout">
                  <span>Competition integrity</span>
                  <p>Countdown timing, physics, checkpoint order and best-time precision are never affected by presentation settings.</p>
                </div>
              </>
            )}

            {tab === 'controls' && (
              <>
                <h2>Controls</h2>
                <p>Select a binding, then press a key. Alternate arrow-key driving remains available by default.</p>
                <div className="bindings-list">
                  {(Object.keys(ACTION_LABELS) as InputAction[]).map((action) => (
                    <div className="binding-row" key={action}>
                      <span>{ACTION_LABELS[action]}</span>
                      <div>
                        {(settings.bindings[action].length > 0 ? settings.bindings[action] : ['']).map((code, index) => (
                          <button
                            key={`${action}-${code || 'unbound'}`}
                            className={rebinding === action && index === 0 ? 'is-listening' : ''}
                            onClick={() => setRebinding(action)}
                          >
                            {rebinding === action && index === 0 ? 'Press a key…' : prettyKey(code)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <button className="reset-bindings-button" onClick={() => controller.resetBindings()}>Restore default bindings</button>
              </>
            )}
          </div>
        </div>
      </MenuFrame>
    </div>
  );
}
